
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Plus,
  Minus,
  Filter,
  Download,
  RefreshCw,
  Search,
  Package,
  User,
  MapPin,
  RotateCcw,
} from 'lucide-react';
import { stockAdjustmentsAPI } from '../../services/garageApi';

const ADJUSTMENT_CONFIG = {
  increase: {
    label: 'Increase',
    icon: TrendingUp,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    badgeColor: 'bg-green-100 text-green-700',
    qtyColor: 'text-green-600',
    sign: '+',
  },
  decrease: {
    label: 'Decrease',
    icon: TrendingDown,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    badgeColor: 'bg-red-100 text-red-700',
    qtyColor: 'text-red-600',
    sign: '-',
  },
  correction: {
    label: 'Correction',
    icon: RotateCcw,
    iconColor: 'text-blue-600',
    bgColor: 'bg-blue-50',
    badgeColor: 'bg-blue-100 text-blue-700',
    qtyColor: 'text-blue-600',
    sign: '',
  },
};

const PRODUCT_TYPE_COLORS = {
  battery: 'bg-blue-100 text-blue-800',
  tire: 'bg-green-100 text-green-800',
  wiper: 'bg-purple-100 text-purple-800',
};

const StockAdjustments = () => {
  const navigate = useNavigate();
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({
    adjustment_type: '',
    product_type: '',
    date_from: '',
    date_to: '',
    search: '',
  });
  const [stats, setStats] = useState({
    totalAdjustments: 0,
    positiveAdjustments: 0,
    negativeAdjustments: 0,
    totalValueAdjusted: 0,
  });

  const fetchAdjustments = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      const params = {};
      if (filters.adjustment_type) params.adjustment_type = filters.adjustment_type;
      if (filters.product_type) params.product_type = filters.product_type;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      if (filters.search) params.search = filters.search;

      const response = await stockAdjustmentsAPI.getAll(params);

      let adjustmentsData = [];
      if (response.data && Array.isArray(response.data)) {
        adjustmentsData = response.data;
      } else if (Array.isArray(response)) {
        adjustmentsData = response;
      }

      // Calculate statistics
      let positiveAdj = 0;
      let negativeAdj = 0;
      let totalUnits = 0;

      adjustmentsData.forEach((adj) => {
        const qty = parseInt(adj.quantity_adjusted) || 0;
        totalUnits += Math.abs(qty);
        if (qty > 0) positiveAdj++;
        else if (qty < 0) negativeAdj++;
      });

      setStats({
        totalAdjustments: adjustmentsData.length,
        positiveAdjustments: positiveAdj,
        negativeAdjustments: negativeAdj,
        totalValueAdjusted: totalUnits,
      });

      setAdjustments(adjustmentsData);
    } catch (err) {
      console.error('Error fetching stock adjustments:', err);
      setError('Failed to load stock adjustments: ' + err.message);
      setAdjustments([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchAdjustments();
  }, [fetchAdjustments]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({ adjustment_type: '', product_type: '', date_from: '', date_to: '', search: '' });
  };

  const exportAdjustments = () => {
    const headers = ['Date', 'Product Type', 'Product', 'Adjustment Type', 'Qty Changed', 'Before', 'After', 'Reason', 'Notes', 'User', 'Location'];
    const csvContent = [
      headers.join(','),
      ...adjustments.map((adj) =>
        [
          new Date(adj.created_at).toLocaleDateString(),
          adj.product_type || '',
          adj.product_name || '',
          adj.adjustment_type || '',
          adj.quantity_adjusted,
          adj.previous_stock ?? '',
          adj.new_stock ?? '',
          adj.reason || '',
          adj.notes || '',
          adj.user_name || '',
          adj.location_name || '',
        ]
          .map((f) => `"${f}"`)
          .join(',')
      ),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `stock-adjustments-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
        <span className="ml-3 text-gray-500">Loading adjustments...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock Adjustments</h1>
          <p className="text-gray-600">Manual inventory corrections, increases and decreases</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={() => navigate('/stock-adjustments/create')}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Adjustment
          </button>
          <button
            onClick={exportAdjustments}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </button>
          <button
            onClick={fetchAdjustments}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4">
          <div className="text-sm text-red-700">{error}</div>
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <Package className="h-6 w-6 text-blue-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Adjustments</p>
                <p className="text-lg font-semibold text-gray-900">{stats.totalAdjustments.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <TrendingUp className="h-6 w-6 text-green-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Increases</p>
                <p className="text-lg font-semibold text-green-600">{stats.positiveAdjustments.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <TrendingDown className="h-6 w-6 text-red-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Decreases</p>
                <p className="text-lg font-semibold text-red-600">{stats.negativeAdjustments.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <RotateCcw className="h-6 w-6 text-purple-600 flex-shrink-0" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total Units Moved</p>
                <p className="text-lg font-semibold text-gray-900">{stats.totalValueAdjusted.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900">Filters</h3>
          <button onClick={clearFilters} className="text-sm text-gray-500 hover:text-gray-700">
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                name="search"
                value={filters.search}
                onChange={handleFilterChange}
                placeholder="Search..."
                className="pl-10 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
            <select
              name="adjustment_type"
              value={filters.adjustment_type}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">All Types</option>
              <option value="increase">Increase</option>
              <option value="decrease">Decrease</option>
              <option value="correction">Correction</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Product Type</label>
            <select
              name="product_type"
              value={filters.product_type}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            >
              <option value="">All Products</option>
              <option value="tire">Tires</option>
              <option value="battery">Batteries</option>
              <option value="wiper">Wipers</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
            <input
              type="date"
              name="date_from"
              value={filters.date_from}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
            <input
              type="date"
              name="date_to"
              value={filters.date_to}
              onChange={handleFilterChange}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
          </div>
          <div className="flex items-end">
            <button
              onClick={fetchAdjustments}
              className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              <Filter className="h-4 w-4 mr-2" />
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Adjustments Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Adjustment History</h3>
        </div>

        {adjustments.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            <RotateCcw className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p>No stock adjustments found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Product</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Qty Changed</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Before / After</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {adjustments.map((adj) => {
                  const qty = parseInt(adj.quantity_adjusted) || 0;
                  const adjType = adj.adjustment_type || (qty > 0 ? 'increase' : qty < 0 ? 'decrease' : 'correction');
                  const config = ADJUSTMENT_CONFIG[adjType] || ADJUSTMENT_CONFIG.correction;
                  const Icon = config.icon;

                  return (
                    <tr key={adj.id} className="hover:bg-gray-50 transition-colors">
                      {/* Type */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded-lg ${config.bgColor}`}>
                            <Icon className={`h-4 w-4 ${config.iconColor}`} />
                          </div>
                          <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${config.badgeColor}`}>
                            {config.label}
                          </span>
                        </div>
                      </td>

                      {/* Product */}
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {adj.product_name || 'Unknown Product'}
                          </p>
                          {adj.product_type && (
                            <span className={`inline-block mt-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${PRODUCT_TYPE_COLORS[adj.product_type] || 'bg-gray-100 text-gray-700'}`}>
                              {adj.product_type}
                            </span>
                          )}
                          {adj.location_name && (
                            <div className="flex items-center gap-1 mt-0.5">
                              <MapPin className="h-3 w-3 text-gray-400" />
                              <span className="text-xs text-gray-400">
                                {adj.location_name}{adj.zone_name ? ` (${adj.zone_name})` : ''}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Quantity */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${config.qtyColor}`}>
                          {qty > 0 ? '+' : ''}{qty}
                        </span>
                      </td>

                      {/* Before / After */}
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {adj.previous_stock != null && adj.new_stock != null ? (
                          <span className="text-xs text-gray-500">
                            {adj.previous_stock} &rarr; {adj.new_stock}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">&mdash;</span>
                        )}
                      </td>

                      {/* Reason */}
                      <td className="px-6 py-4 max-w-xs">
                        {adj.reason ? (
                          <p className="text-xs text-gray-600 truncate">{adj.reason}</p>
                        ) : (
                          <span className="text-xs text-gray-300">&mdash;</span>
                        )}
                        {adj.notes && adj.notes !== adj.reason && (
                          <p className="text-xs text-gray-400 mt-0.5 truncate">{adj.notes}</p>
                        )}
                      </td>

                      {/* User */}
                      <td className="px-6 py-4 whitespace-nowrap">
                        {adj.user_name ? (
                          <div className="flex items-center gap-1.5">
                            <User className="h-3.5 w-3.5 text-gray-400" />
                            <span className="text-xs text-gray-600">{adj.user_name}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-300">System</span>
                        )}
                      </td>

                      {/* Date */}
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <p className="text-xs text-gray-500">
                          {new Date(adj.created_at).toLocaleDateString()}
                        </p>
                        <p className="text-xs text-gray-400">
                          {new Date(adj.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockAdjustments;
